import React, { useState, useRef, useEffect } from "react";
import "./ChatBotRCP.css";
import chatIcon from "../../assets/images/chat-icon-rcp.jpg";
import dapIcon from "../../assets/images/dapIcon.png";

const FloatingChatWidgetRCP2 = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("chatMessagesrcp2");
    return saved
      ? JSON.parse(saved)
      : [{ type: "bot", text: "👋 Hi! I'm your CIEL Assistant. How can I help you today?" }];
  });
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  const chatRef = useRef(null);
  const endOfMessagesRef = useRef(null);
  const currentReader = useRef(null);
  const currentAbortController = useRef(null);
  const isUnmounted = useRef(false);

  // 🟢 Added: Maintain session attributes
  const [sessionAttributes, setSessionAttributes] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sessionAttrRCP2")) || {};
    } catch {
      return {};
    }
  });

  const formatBotResponse = (text) => {
    if (!text) return [""];
  
    const lines = text.split("\n");
    const formatted = [];
  
    lines.forEach((line) => {
      let trimmed = line.trim();
      if (!trimmed) return;
  
      // ----------------------------
      // Confirmation block
      // ----------------------------
      if (/please confirm/i.test(trimmed) && /Name:/.test(trimmed)) {
        const headerMatch = trimmed.match(/^(Please confirm your booking details:)/i);
        const header = headerMatch ? headerMatch[1] : "Please confirm your booking details:";
        formatted.push(header);
  
        let rest = trimmed.replace(header, "").trim();
        if (rest.startsWith(":")) rest = rest.slice(1).trim();
  
        let confirmPart = "";
        const confirmMatch = rest.match(/(Would you like to confirm.*)/i);
        if (confirmMatch) {
          confirmPart = confirmMatch[1].trim();
          rest = rest.replace(confirmPart, "").trim();
        }
  
        const kvPairs = rest.split(/,\s*/).filter(Boolean);
        kvPairs.forEach((kv) => formatted.push(`🔹 ${kv}`));
  
        if (confirmPart) {
          formatted.push("");
          formatted.push(confirmPart + " ✅");
        }
        return;
      }
  
      // ----------------------------
      // Handle date lists with trailing sentence
      // ----------------------------
      const dateListMatch = trimmed.match(/^(.*:)\s*((?:\d{2}\/\d{2}\/\d{4},?\s*)+)\.\s*(Please choose.*)/i);
      if (dateListMatch) {
        const header = dateListMatch[1].trim();
        const datesPart = dateListMatch[2].trim();
        const promptPart = dateListMatch[3].trim();
  
        formatted.push(header);
        formatted.push("");
  
        datesPart.split(",").forEach((date) => {
          if (date.trim()) formatted.push(`🗓️ ${date.trim()}`);
        });
  
        formatted.push("");
        formatted.push(promptPart);
        return;
      }
  
      // ----------------------------
      // Handle month selection list specifically
      // ----------------------------
      if (/Which month would you prefer to take the exam\?/i.test(trimmed)) {
        const parts = trimmed.split(":");
        formatted.push(parts[0].trim() + ":"); // main sentence before colon
        formatted.push("");
  
        if (parts[1]) {
          parts[1]
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean)
            .forEach((item) => formatted.push(`🔹 ${item}`));
        }
        return;
      }
  
      // ----------------------------
      // Handle other exam center or month lists (colon + comma-separated)
      // ----------------------------
      const listMatch = trimmed.match(/^(.*:)\s*(.+,.+)/i);
      if (listMatch) {
        const header = listMatch[1].trim();
        const itemsPart = listMatch[2].trim();
  
        formatted.push(header);
        formatted.push("");
  
        // Use default 📍 emoji for all other items
        itemsPart.split(",").forEach((item) => {
          if (item.trim()) formatted.push(`📍 ${item.trim()}`);
        });
  
        return;
      }
  
      // ----------------------------
      // Handle thank you + help message
      // ----------------------------
      const thankHelpMatch = trimmed.match(/^(Thank you!.*?once your slot is booked)\.\s*(Need help.*)$/i);
      if (thankHelpMatch) {
        const mainPart = thankHelpMatch[1].trim();
        const helpPart = thankHelpMatch[2].trim();
  
        formatted.push(`✅ ${mainPart}.`);
        formatted.push("");
        formatted.push(`❓ ${helpPart}`);
        return;
      }
  
      // ----------------------------
      // Fallback
      // ----------------------------
      formatted.push(trimmed);
    });
  
    return formatted;
  };
  
  
  function generateSessionId() {
    return "session-" + Math.random().toString(36).substring(2, 15);
  }

  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("agentSessionIdRCP2") || generateSessionId();
  });

  useEffect(() => {
    localStorage.setItem("agentSessionIdRCP2", sessionId);
  }, [sessionId]);

  const cancelActiveStream = async () => {
    if (currentAbortController.current) {
      try { currentAbortController.current.abort(); } catch (_) {}
      currentAbortController.current = null;
    }
    if (currentReader.current) {
      try { await currentReader.current.cancel(); } catch (_) {}
      try { if (typeof currentReader.current.releaseLock === "function") currentReader.current.releaseLock(); } catch (_) {}
      currentReader.current = null;
    }
    setMessages((prev) => prev.filter((m) => m.type !== "bot-temp"));
  };

  const sendMessage = async (message, retry = false) => {
    if (!message.trim() || isTyping) return;

    if (message.trim().toLowerCase() === "clear") {
      await cancelActiveStream();
      const initialMsg = {
        type: "bot",
        text: "👋 Hi! I'm your CIEL Assistant. How can I help you today?",
      };
      setMessages([initialMsg]);
      localStorage.setItem("chatMessagesrcp2", JSON.stringify([initialMsg]));
      setInput("");
      return;
    }

    await cancelActiveStream();
    if (!retry) setMessages((prev) => [...prev, { type: "user", text: message }]);
    setInput("");
    setIsTyping(true);

    const controller = new AbortController();
    currentAbortController.current = controller;

    try {
      let currentSessionId = localStorage.getItem("agentSessionIdRCP2") || sessionId;

      // 🟢 Added: include sessionAttributes in API call
      const params = new URLSearchParams({
        message: message,
        sessionId: currentSessionId,
        topK: "5",
        sessionAttributes: JSON.stringify(sessionAttributes),
      });

      const url = `https://8rhbl820k3.execute-api.ap-south-1.amazonaws.com/production/cpeapi?${params.toString()}`;
    
      const response = await fetch(url, {
        method: "GET",
        headers: { Accept: "text/plain, */*", "Cache-Control": "no-cache" },
        signal: controller.signal,
      });
    
      const newSessionId = response.headers.get("x-session-id");
      if (newSessionId && newSessionId !== currentSessionId) {
        localStorage.setItem("agentSessionIdRCP2", newSessionId);
        setSessionId(newSessionId);
        currentSessionId = newSessionId;
      }
    
      if (response.status === 500) {
        throw new Error("Server error (500)");
      }
    
      if (!response.ok) {
        let errText = "";
        try { errText = await response.json(); } catch (_) {}
        throw new Error(`HTTP ${response.status}: ${errText.response || "Request failed"}`);
      }
    
      const data = await response.json();

      // 🟢 Added: Save new sessionAttributes if returned
      const botMessage = data.response;
      const newAttrs = data.sessionAttributes || {};
      if (Object.keys(newAttrs).length > 0) {
        setSessionAttributes(newAttrs);
        localStorage.setItem("sessionAttrRCP2", JSON.stringify(newAttrs));
      }

      if (botMessage && !isUnmounted.current) {
        setMessages((prev) => [...prev, { type: "bot", text: botMessage }]);
      }
    
    } catch (error) {
      console.error("Chat error:", error);
      if (!isUnmounted.current) {
        setMessages((prev) => [
          ...prev,
          { type: "bot", text: "⚠️ Something went wrong. Please try again later." },
        ]);
      }
    } finally {
      if (!isUnmounted.current) setIsTyping(false);
    }
    
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  useEffect(() => {
    if (endOfMessagesRef.current) endOfMessagesRef.current.scrollIntoView({ behavior: "smooth" });
    localStorage.setItem("chatMessagesrcp2", JSON.stringify(messages));
  }, [messages, isTyping]);

  useEffect(() => {
    return () => {
      isUnmounted.current = true;
      if (currentAbortController.current) try { currentAbortController.current.abort(); } catch (_) {}
      if (currentReader.current) try { currentReader.current.cancel(); } catch (_) {}
    };
  }, []);

  return (
    <div className="chat-widget-wrapper-rcp">
      {!isOpen && (
        <button className="chat-toggle-button" onClick={() => setIsOpen(true)}>
          <img src={chatIcon} alt="Chat" />
        </button>
      )}

      {isOpen && (
        <div className="floating-chat-box">
          <div className="chat-header-rcp">
            <div className="chat-header-left">
              <img className="chat-icon" src={dapIcon} alt="Chat" />
              <span className="chat-title">CIEL AI Buddy – Your 24×7 Assistant</span>
            </div>
            <button className="close-btn" onClick={() => setIsOpen(false)}>×</button>
          </div>

          <div className="chat-body" ref={chatRef}>
            {messages.map((msg, idx) => {
              const content = msg.type === "bot" ? formatBotResponse(msg.text).join("<br/>") : msg.text;
              return <div key={idx} className={`message ${msg.type}`} dangerouslySetInnerHTML={{ __html: content }}></div>;
            })}
            {isTyping && <div className="message bot">Wait...</div>}
            <div ref={endOfMessagesRef} />
          </div>

          <div className="chat-input-area">
            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type a message..." rows={1} />
            <button onClick={() => sendMessage(input)} disabled={isTyping || !input.trim()}>➤</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingChatWidgetRCP2;
